import { useState } from "react";
import type { ConditionSearchHit, Rating, RatingDraft } from "../../types";
import { getApi } from "../../api";
import { notify } from "../../store/uiStore";
import { reportError } from "../../services/eventBus";
import { Button } from "../Button";
import { Card } from "../Card";
import { FieldRow } from "../FieldRow";
import { TagPill } from "../Pills";
import { CheckboxInput, NumberInput, TextArea, TextInput } from "../fields";

interface NewRatingFormProps {
  condition: ConditionSearchHit;
  onSaved: (rating: Rating) => void;
  onChangeCondition: () => void;
  children?: never;
}

/** Adds one custom rating to a condition already in the catalog. */
export function NewRatingForm({ condition, onSaved, onChangeCondition }: NewRatingFormProps) {
  const [ratingText, setRatingText] = useState("");
  const [time, setTime] = useState("");
  const [debits, setDebits] = useState<number | null>(null);
  const [isMedication, setIsMedication] = useState(false);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<{ ratingText?: string; debits?: string }>({});
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const nextErrors: typeof errors = {};
    if (ratingText.trim() === "") nextErrors.ratingText = "Rating text is required";
    if (debits === null || !Number.isInteger(debits) || debits < 0) nextErrors.debits = "Enter whole-number debits";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || debits === null) return;
    setIsSaving(true);
    try {
      const draft: RatingDraft = { ratingText: ratingText.trim(), time: time.trim() || null, debits, notes: notes.trim() || null, isMedication };
      const rating = await getApi().createRating(condition.conditionId, draft);
      notify(`Added "${rating.ratingText}" to ${condition.name}. Skynet sees it immediately.`, "success");
      onSaved(rating);
    } catch (error) {
      reportError(error, "NewRatingForm.save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card
      title={`New rating on ${condition.name}`}
      actions={
        <span className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{condition.category}</span>
          {condition.isCustom && <TagPill label="Custom" tone="green" />}
          <Button variant="ghost" size="sm" onClick={onChangeCondition} disabled={isSaving}>
            Change condition
          </Button>
        </span>
      }
    >
      <div className="space-y-4">
        <FieldRow>
          <TextInput id="new-rating-text" label="Rating" value={ratingText} onChange={setRatingText} error={errors.ratingText} required placeholder={isMedication ? "Drug name (Rx : is added)" : "e.g. Moderate, persistent"} />
          <TextInput id="new-rating-time" label="Time" value={time} onChange={setTime} placeholder="e.g. within 2 yrs" />
        </FieldRow>
        <FieldRow>
          <NumberInput id="new-rating-debits" label="Debits" value={debits} onChange={setDebits} error={errors.debits} required min={0} />
          <div className="flex items-end pb-2">
            <CheckboxInput id="new-rating-is-medication" label="Medication (Rx)" checked={isMedication} onChange={setIsMedication} description="Medication ratings count as Rx debits." />
          </div>
        </FieldRow>
        <TextArea id="new-rating-notes" label="Notes" value={notes} onChange={setNotes} rows={2} />
        <div className="flex justify-end">
          <Button onClick={() => void handleSave()} isLoading={isSaving}>
            Add rating
          </Button>
        </div>
      </div>
    </Card>
  );
}
